/**
 * @fileoverview Read-only lesson summary view for students ("Studio Quiet").
 *
 * Header, then the lesson in reading order: summary, key elements, practice
 * items, the teacher's own notes, demonstration clips, attachments,
 * assignments, core concepts, requests, released practice feedback and the
 * comment thread. Plus PDF export and a rehearse shortcut for assigned
 * lessons.
 *
 * Section headings come from `lib/lessonSections` rather than being typed
 * here — six surfaces render a lesson, and each typing its own heading is how
 * one section came to have four names.
 */

import { useState, useEffect, useCallback, useContext, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StudentAuthContext } from "@/App";
import studentApi from "@/lib/studentApi";
import { exportLessonPdf } from "@/lib/exportLessonPdf";
import { ASSIGNMENTS_ENABLED, rehearsalsEnabled } from "@/lib/featureFlags";
import { formatBytes } from "@/lib/attachments";
import { StudentAssignmentsChecklist } from "@/components/StudentAssignmentsChecklist";
import LessonClips from "@/components/LessonClips";
import UnreviewedAiNotice from "@/components/UnreviewedAiNotice";
import CommentThread from "@/components/CommentThread";
import { Card, Icon, Button, Tag } from "@/components/studio/primitives";
import ProgramChip from "@/components/ProgramChip";
import RichText from "@/components/RichText";
import { isInstructorWritten } from "@/lib/lessonAuthorship";
import { LESSON_SECTION_ORDER, sectionLabel } from "@/lib/lessonSections";

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} sec`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

/** True when a released recording carries any Hans-written feedback. */
const hasGeneratedFeedback = (rec) =>
  !!(rec.ai_notes || rec.ai_strengths?.length || rec.ai_improvements?.length);

/** Card section with an eyebrow-style header (icon + label). */
const Section = ({ title, icon, children }) => (
  <Card className="mb-4">
    <div className="mb-3.5 flex items-center gap-2">
      <Icon name={icon} size={15} strokeWidth={1.8} className="text-[var(--accent-yellow-text)]" />
      <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{title}</span>
    </div>
    {children}
  </Card>
);

const StudentLessonView = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const showRehearsals = rehearsalsEnabled(student);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [practiceRecordings, setPracticeRecordings] = useState([]);
  const [hasRehearsed, setHasRehearsed] = useState(false);
  const [assignments, setAssignments] = useState([]);

  const refreshAssignments = useCallback((signal) => {
    if (!ASSIGNMENTS_ENABLED) return;
    studentApi
      .get("/student/assignments", signal ? { signal } : {})
      .then((res) => setAssignments((res.data || []).filter((a) => a.lesson_id === lessonId)))
      .catch(() => {});
  }, [lessonId]);

  useEffect(() => {
    const controller = new AbortController();
    refreshAssignments(controller.signal);
    return () => controller.abort();
  }, [refreshAssignments]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const res = await studentApi.get(`/student/lessons/${lessonId}`, { signal: controller.signal });
        setLesson(res.data);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to load lesson:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();

    // Skip when rehearsals is off — the endpoint 404s and there's nothing to show.
    if (showRehearsals) {
      studentApi.get("/student/practice/recordings", { signal: controller.signal }).then((res) => {
        const forLesson = (res.data || []).filter((r) => r.lesson_id === lessonId);
        setHasRehearsed(forLesson.length > 0);
        setPracticeRecordings(forLesson.filter((r) => r.released));
      }).catch((err) => {
        if (!controller.signal.aborted) console.error(err);
      });
    }
    return () => controller.abort();
  }, [lessonId, showRehearsals]);

  const handleExportPdf = async () => {
    try {
      await exportLessonPdf({
        lesson,
        studentName: "Student",
        teacherName: lesson.teacher_name,
        filenameSuffix: lesson.teacher_name,
      });
    } catch (_error) {
      toast.error("Failed to export PDF");
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[820px] px-6 pb-32 pt-11 md:px-12">
        <div className="space-y-4">
          <div className="skeleton h-10 w-2/3 rounded-xl" />
          <div className="skeleton h-40 rounded-[20px]" />
          <div className="skeleton h-32 rounded-[20px]" />
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="mx-auto max-w-[820px] px-6 pb-32 pt-11 text-center md:px-12">
        <p className="font-sans text-[var(--text-tertiary)]">Lesson not found</p>
        <button
          type="button"
          onClick={() => navigate("/student/dashboard")}
          className="mt-2 font-sans text-sm text-[var(--accent-yellow-text)] hover:underline"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  // Released practice feedback for this lesson. Built as a variable so the
  // renderer map below stays a flat key->JSX table.
  const practiceFeedbackSection = practiceRecordings.length > 0 && (
      <Section title={sectionLabel("practice_feedback")} icon="mic">
        <div className="space-y-5">
          {practiceRecordings.map((rec) => (
            <div key={rec.recording_id} className="border-b border-[var(--studio-divider)] pb-5 last:border-0 last:pb-0">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{rec.title}</span>
                <span className="font-mono text-[13px] text-[var(--text-tertiary)]">
                  {Math.floor(rec.duration_seconds / 60)}m {rec.duration_seconds % 60}s
                </span>
                {rec.instrument && <Tag accent>{rec.instrument}</Tag>}
              </div>

              {/* Who wrote the generated part. Unreviewed feedback carries the
                  shared F7 sentence; otherwise a plain authorship line, so the
                  notes above "Instructor notes" never read as the teacher's. */}
              {rec.ai_unreviewed ? (
                <UnreviewedAiNotice className="mb-2" />
              ) : hasGeneratedFeedback(rec) ? (
                <p className="mb-2 font-sans text-[12px] italic text-[var(--text-tertiary)]" data-testid="feedback-drafted-by-hans-note">
                  Feedback drafted by Hans
                </p>
              ) : null}

              {rec.ai_notes && (
                <p className="mb-3 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{rec.ai_notes}</p>
              )}

              {rec.ai_strengths?.length > 0 && (
                <div className="mb-3">
                  <h4 className="mb-1.5 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]">Strengths</h4>
                  <ul className="space-y-1">
                    {rec.ai_strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
                        <Icon name="check" size={14} className="mt-1 shrink-0 text-[var(--state-success)]" />{s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {rec.ai_improvements?.length > 0 && (
                <div className="mb-3">
                  <h4 className="mb-1.5 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">Areas to improve</h4>
                  <ul className="space-y-1">
                    {rec.ai_improvements.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
                        <span className="mt-0.5 shrink-0 text-[var(--text-tertiary)]">•</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {rec.instructor_notes && (
                <div className="mt-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-base)] p-3">
                  <h4 className="mb-1.5 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--accent-yellow-text)]">Instructor notes</h4>
                  <p className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{rec.instructor_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
  );

  /**
   * One renderer per section, keyed the same way the shared order table is.
   * Each returns null when the lesson carries nothing for it, so ordering and
   * presence stay separate concerns: the order comes from lessonSections, the
   * presence from the lesson.
   */
  const bulletList = (items) => (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
          <span className="mt-0.5 text-[var(--accent-yellow-text)]">•</span>{item}
        </li>
      ))}
    </ul>
  );

  const sectionRenderers = {
    summary: () =>
      lesson.summary && (
        <Section title={sectionLabel("summary")} icon="book">
          {/* Provenance, stated where the notes are read: a family should not
              have to guess whether their teacher wrote this or Hans did. Both
              cases are named — the email and the PDF already label both, and a
              summary with no line under it reads as the teacher's own words. */}
          {isInstructorWritten(lesson) ? (
            <p className="mb-3 font-sans text-[13px] italic text-[var(--text-tertiary)]" data-testid="written-by-instructor-note">
              Written by {lesson.teacher_name || "your instructor"} — not an AI summary.
            </p>
          ) : (
            <p className="mb-3 font-sans text-[13px] italic text-[var(--text-tertiary)]" data-testid="summary-drafted-by-hans-note">
              Summary drafted by Hans
            </p>
          )}
          <RichText
            value={lesson.summary}
            testId="student-lesson-summary"
            className="font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]"
          />
        </Section>
      ),

    /* What this student is working toward. Replaced Key elements and absorbed
       the old requests section, so there is one section about the student
       rather than two that overlapped. */
    student_focus: () =>
      lesson.student_focus?.length > 0 && (
        <Section title={sectionLabel("student_focus")} icon="target">
          <ol className="space-y-2" data-testid="student-focus-list">
            {lesson.student_focus.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 font-sans text-[15px] text-[var(--text-secondary)]">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] font-mono text-[11px] font-semibold text-[var(--accent-yellow-text)]">
                  {i + 1}
                </span>
                {item}
              </li>
            ))}
          </ol>
        </Section>
      ),

    /* RETIRED — replaced by Student focus. Still rendered so a lesson from
       before the change keeps everything it always showed. */
    key_elements: () =>
      lesson.key_elements?.length > 0 && (
        <Section title={sectionLabel("key_elements")} icon="star">
          {bulletList(lesson.key_elements)}
        </Section>
      ),

    practice_items: () =>
      lesson.practice_items?.length > 0 && (
        <Section title={sectionLabel("practice_items")} icon="check">
          <ul className="space-y-2">
            {lesson.practice_items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 font-sans text-[15px] text-[var(--text-secondary)]">
                <span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[var(--studio-divider-strong)]" />{item}
              </li>
            ))}
          </ul>
        </Section>
      ),

    /* Notes the teacher wrote by hand. These ride the summary email and the
       exported PDF, so a family that read the email and then opened the portal
       used to find them missing here — the one piece of the lesson that
       arrived by mail and nowhere else. */
    instructor_notes: () =>
      lesson.instructor_notes && (
        <Section title={sectionLabel("instructor_notes")} icon="edit">
          <RichText
            value={lesson.instructor_notes}
            testId="student-lesson-instructor-notes"
            className="font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]"
          />
        </Section>
      ),

    /* Renders nothing when the lesson has no clips, which is the common case. */
    clips: () => <LessonClips client={studentApi} path={`/student/lessons/${lessonId}/clips`} />,

    attachments: () =>
      lesson.attachments?.length > 0 && (
        <Section title={sectionLabel("attachments")} icon="paperclip">
          <div className="flex flex-col gap-2.5" data-testid="lesson-attachments-files">
            {lesson.attachments.map((item) => (
              <a
                key={item.attachment_id}
                href={item.url}
                target="_blank"
                /* The presigned URL carries its signature in the query string;
                   without noreferrer it would leak through the Referer header. */
                rel="noopener noreferrer"
                download={item.filename}
                className="flex items-center gap-2.5 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2.5 transition-colors hover:border-[var(--studio-divider-strong)]"
                data-testid={`attachment-${item.attachment_id}`}
              >
                <Icon name="fileText" size={16} className="shrink-0 text-[var(--accent-yellow-text)]" />
                <span className="min-w-0 flex-1 truncate font-sans text-[15px] text-[var(--text-secondary)]">
                  {item.filename}
                </span>
                <span className="shrink-0 font-mono text-[12px] text-[var(--text-tertiary)]">
                  {formatBytes(item.size_bytes)}
                </span>
                <Icon name="download" size={15} className="shrink-0 text-[var(--text-tertiary)]" />
              </a>
            ))}
          </div>
        </Section>
      ),

    core_concepts: () =>
      lesson.core_concepts?.length > 0 && (
        <Section title={sectionLabel("core_concepts")} icon="brain">
          <div className="flex flex-wrap gap-2">
            {lesson.core_concepts.map((concept, i) => (
              <Tag key={i}>{concept}</Tag>
            ))}
          </div>
        </Section>
      ),

    /* RETIRED — absorbed by Student focus. See key_elements above. */
    student_requests: () =>
      lesson.student_requests?.length > 0 && (
        <Section title={sectionLabel("student_requests")} icon="sparkle">
          {bulletList(lesson.student_requests)}
        </Section>
      ),

    practice_feedback: () => practiceFeedbackSection,
  };

  return (
    <div className="mx-auto max-w-[820px] px-6 pb-32 pt-11 md:px-12" data-testid="student-lesson-view">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/student/lessons")}
        className="mb-6 flex items-center gap-1 font-sans text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={16} /> Back to lessons
      </button>

      {/* Header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-[30px] font-normal leading-tight tracking-[-0.01em] text-[var(--text-primary)] md:text-[36px]">
            {lesson.title}
          </h1>
          {lesson.program_title && (
            <div className="mt-2">
              <ProgramChip programTitle={lesson.program_title} orgName={lesson.org_name} accent={lesson.org_accent} />
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[13px] text-[var(--text-tertiary)]">
            <span className="inline-flex items-center gap-1.5">
              <Icon name="user" size={14} /> {lesson.teacher_name}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Icon name="clock" size={14} /> {formatDuration(lesson.duration_seconds)}
            </span>
            <span>{formatDate(lesson.created_at)}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="bare" icon="download" onPress={handleExportPdf} data-testid="export-pdf-button">
            Export PDF
          </Button>
          {!showRehearsals ? null : hasRehearsed ? (
            <span className="inline-flex items-center gap-1.5 font-sans text-sm text-[var(--state-success)]">
              <Icon name="check" size={16} /> Rehearsed
            </span>
          ) : lesson.rehearsal_assigned ? (
            <Button icon="mic" onPress={() => navigate(`/student/rehearsals?lesson=${lessonId}`)}>
              Rehearse
            </Button>
          ) : null}
        </div>
      </div>

      {/* Sections render in the order declared by `lib/lessonSections`, not in
          the order they happen to be written here — see LESSON_SECTION_ORDER.
          Each renderer returns null when the lesson has nothing for it, which
          is what lets the two retired sections (key elements, requests) sit in
          the order and simply not appear on a lesson recorded after they were
          replaced by Student focus. */}
      {LESSON_SECTION_ORDER.map((key) => (
        <Fragment key={key}>
          {sectionRenderers[key]?.() || null}
          {/* Spec 01 assignments are promoted practice items, so they read with
              them. Deliberately NOT in the order table: they are a feature
              block with their own component and heading, not one of the
              lesson's content sections. */}
          {key === "practice_items" && ASSIGNMENTS_ENABLED && assignments.length > 0 && (
            <div className="mb-4" data-testid="lesson-assignments">
              <StudentAssignmentsChecklist assignments={assignments} grouped={false} onChanged={refreshAssignments} />
            </div>
          )}
        </Fragment>
      ))}

      {/* Conversation (F9). The point of the whole feature from this side:
          a student with a question about a practice item has, until now, had
          no in-product way to ask — so the answer happened in iMessage, where
          Hans cannot see it and no parent had to be included. Self-hides when
          COMMENTS_ENABLED is off. */}
      <CommentThread
        client={studentApi}
        basePath="/student/comments"
        artifactKind={lesson?.kind === "class" ? "class_lesson" : "lesson"}
        artifactId={lessonId}
        title="Ask your teacher"
      />
    </div>
  );
};

export default StudentLessonView;
